import { Link } from "./LocaleLink";
import { Mail, MessageCircle, MapPin, Instagram, Linkedin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { openConsentSettings } from "../lib/consent";
import { CONTACT, waHref } from "../i18n/translations";

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();
  const waLink = waHref(t("contact.whatsappText"));

  return (
    <footer data-testid="site-footer" className="relative bg-secondary border-t border-border/60">
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <div className="flex flex-col leading-none mb-6">
              <span className="font-headings text-3xl tracking-wide text-foreground">Velmira</span>
              <span lang="en" className="brand-caps font-accent text-[11px] uppercase tracking-[0.35em] text-primary -mt-0.5">Living</span>
            </div>
            <p className="font-body font-light text-foreground/70 max-w-sm leading-relaxed">{t("footer.tagline")}</p>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground mt-6">{t("footer.based")}</p>

            <div className="mt-8">
              <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("footer.follow")}</p>
              <div className="flex items-center gap-3" data-testid="footer-social">
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="footer-instagram"
                  className="w-10 h-10 flex items-center justify-center border border-border/60 text-foreground/70 hover:border-primary hover:text-primary transition-all duration-300"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  data-testid="footer-linkedin"
                  className="w-10 h-10 flex items-center justify-center border border-border/60 text-foreground/70 hover:border-primary hover:text-primary transition-all duration-300"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-accent text-xs uppercase tracking-[0.2em] text-primary mb-6">{t("footer.quickLinks")}</h4>
            <ul className="flex flex-col gap-3 font-body font-light text-foreground/70">
              <li><Link to="/" className="hover:text-primary transition-colors" data-testid="footer-home">{t("nav.home")}</Link></li>
              <li><Link to="/properties" className="hover:text-primary transition-colors" data-testid="footer-properties">{t("nav.properties")}</Link></li>
              <li><Link to="/property-management" className="hover:text-primary transition-colors" data-testid="footer-pm">{t("nav.propertyMgmt")}</Link></li>
              <li><Link to="/airbnb-management-nice" className="hover:text-primary transition-colors" data-testid="footer-airbnb">Airbnb Management Nice</Link></li>
              <li><Link to="/french-riviera-investment-guide" className="hover:text-primary transition-colors" data-testid="footer-invest">Investment Guide</Link></li>
              <li><Link to="/blog" className="hover:text-primary transition-colors" data-testid="footer-blog">{t("nav.blog")}</Link></li>
              <li><Link to="/press" className="hover:text-primary transition-colors" data-testid="footer-press">Press & Media</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors" data-testid="footer-contact">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-accent text-xs uppercase tracking-[0.2em] text-primary mb-6">{t("footer.contactTitle")}</h4>
            <ul className="flex flex-col gap-4 font-body font-light text-foreground/70">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-primary transition-colors" data-testid="footer-email">
                  <Mail size={16} className="text-primary" /> {CONTACT.email}
                </a>
              </li>
              <li>
                <a href={waLink} target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-3 hover:text-primary transition-colors" data-testid="footer-whatsapp">
                  <MessageCircle size={16} className="text-primary" /> {CONTACT.whatsapp}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-primary" /> {t("contact.location")}
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={16} className="text-primary" /> {t("contact.locationMonaco")}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border/50 space-y-5">
          {/* Legal identifiers — minimalist, premium, quiet. Present on every page. */}
          <div
            data-testid="footer-legal-identifiers"
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-center gap-x-6 gap-y-2 text-center sm:text-left"
          >
            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground/80">
              <span className="text-muted-foreground/60 mr-2">SIRET</span>
              <span className="text-foreground/70">953 630 084 00010</span>
            </p>
            <span className="hidden sm:inline text-muted-foreground/40" aria-hidden="true">·</span>
            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground/80">
              <span className="text-muted-foreground/60 mr-2">RSAC</span>
              <span className="text-foreground/70">937 000 002 071 316 661</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-body text-xs text-muted-foreground">© {year} Velmira Living. {t("footer.rights")}</p>
            <div className="flex items-center gap-5">
              <Link
                to="/mentions-legales"
                data-testid="footer-legal-link"
                className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-primary transition-colors"
              >
                {t("footer.legalLink")}
              </Link>
              <button
                type="button"
                onClick={openConsentSettings}
                data-testid="footer-cookie-settings"
                className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-primary transition-colors"
              >
                {t("cookies.settings")}
              </button>
              <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Nice · Monaco · Riviera</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
